// What app.js loads (as /ui/munin-ui.js) to draw a page with React during the move (FE2). Every file in screens/ that
// exports `screen` is registered by its name; app.js asks has(name) and mount(name, …) in route().
import { QueryClientProvider } from "@tanstack/react-query";
import type { ComponentType } from "react";
import { flushSync } from "react-dom";
import { createRoot } from "react-dom/client";
import { queryClient, ready as cacheReady } from "../api/query";
import type { BareHost, BareScreenProps, Host, ScreenProps } from "./host";

// the local-first cache (api/query.ts): app.js awaits ready(userId) before the first signed-in page, forget() on sign out
export { forget } from "../api/query";

// MC1: the kit's stylesheet (components/ui: Panel, SectionHead) is built next to this file as munin-ui.css. It loads with
// the React pages, not in the first load, and ready() waits for it, so the first signed-in page never draws unstyled.
// A stylesheet that fails to load does not hold the page: the blocks then show without their card
const kitCss = new Promise<void>(done => {
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = new URL("munin-ui.css", import.meta.url).href;
  link.onload = link.onerror = () => done();
  document.head.append(link);
});
export const ready = (userId: string) => Promise.all([cacheReady(userId), kitCss]).then(() => undefined);
// E2E-POLL: the full version of a draft through the sync engine's Realtime push, when the engine runs (app/watch-notes.ts)
export { watchNotes } from "./watch-notes";

// a screen file exports `screen` (signed in, inside the frame) or `bareScreen` (signed out, no frame: login, gone)
type ScreenModule = {
  screen?: { name: string; Screen: ComponentType<ScreenProps> };
  bareScreen?: { name: string; Screen: ComponentType<BareScreenProps> };
};
// one file per page (code splitting): a page's code downloads the first time it opens, not with the first page of the
// visit. app.js calls load(name) before mount(name); the file name is the page name (screens/settings.tsx is "settings")
const LOADERS = new Map(
  Object.entries(import.meta.glob<ScreenModule>("../screens/*.tsx")).map(([path, load]) => [path.replace(/^.*\/(\w+)\.tsx$/, "$1"), load])
);
const SCREENS = new Map<string, ComponentType<ScreenProps>>();
const BARE = new Map<string, ComponentType<BareScreenProps>>();

/** downloads page `name` once; false when there is no such page */
export async function load(name: string): Promise<boolean> {
  if (SCREENS.has(name) || BARE.has(name)) return true;
  const loader = LOADERS.get(name);
  if (!loader) return false;
  const m = await loader();
  if (m.screen) SCREENS.set(m.screen.name, m.screen.Screen);
  if (m.bareScreen) BARE.set(m.bareScreen.name, m.bareScreen.Screen);
  return SCREENS.has(name) || BARE.has(name);
}

export const has = (name: string) => SCREENS.has(name);
export const hasBare = (name: string) => BARE.has(name);
export const names = () => [...LOADERS.keys()];

/** draws screen `name` into `main` (the .main element; app.js already drew the sidebar) and returns the unmount */
export function mount(name: string, main: HTMLElement, host: Host, args: ScreenProps["args"] = {}): () => void {
  const Screen = SCREENS.get(name);
  if (!Screen) throw new Error(`munin-ui: no screen named ${name}; known: ${names().join(", ")}`);
  main.replaceChildren();
  const root = createRoot(main);
  // synchronous, so code that runs right after the route (tests, focus, scroll restore) finds the page drawn
  flushSync(() =>
    root.render(
      <QueryClientProvider client={queryClient}>
        <Screen host={host} args={args} />
      </QueryClientProvider>
    )
  );
  return () => root.unmount();
}

/** draws signed-out screen `name` into `root` (#app: no sidebar, no frame) and returns the unmount */
export function mountBare(name: string, root: HTMLElement, host: BareHost, args: BareScreenProps["args"] = {}): () => void {
  const Screen = BARE.get(name);
  if (!Screen) throw new Error(`munin-ui: no signed-out screen named ${name}; known: ${names().join(", ")}`);
  root.replaceChildren();
  const r = createRoot(root);
  flushSync(() => r.render(<Screen host={host} args={args} />));
  return () => r.unmount();
}
